import { supabase } from '../supabase'
import type { Notification, AuditLog } from '../types'

// ── Notifications ─────────────────────────────────────────────
export const getNotifications = async (userId: string, limit = 50) => {
  const { data, error } = await supabase.from('notifications')
    .select('*').eq('recipient_id', userId)
    .order('created_at', { ascending: false }).limit(limit)
  if (error) throw error
  return data as Notification[]
}

export const getUnreadCount = async (userId: string) => {
  const { count, error } = await supabase.from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('recipient_id', userId).eq('is_read', false)
  if (error) throw error
  return count ?? 0
}

export const markRead = async (id: string, userId: string) => {
  const { error } = await supabase.from('notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('id', id)
    .eq('recipient_id', userId)
  if (error) throw error
}

export const markAllRead = async (userId: string) => {
  const { error } = await supabase.from('notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('recipient_id', userId).eq('is_read', false)
  if (error) throw error
}

export const createNotification = async (payload: {
  recipient_id: string; type: string; title: string; body?: string; link?: string
}) => {
  const { error } = await supabase.from('notifications').insert(payload)
  if (error) throw error
}

// Send to all admins
export const notifyAdmins = async (type: string, title: string, body: string, link?: string) => {
  const { data: admins } = await supabase.from('profiles')
    .select('id').eq('is_admin', true).is('deleted_at', null)
  if (!admins) return
  const notifications = admins.map(a => ({ recipient_id: a.id, type, title, body, link }))
  const { error } = await supabase.from('notifications').insert(notifications)
  if (error) throw error

  // Best-effort browser push. The in-app bell remains authoritative even when
  // Web Push is not configured or the current browser has no subscription.
  void supabase.functions.invoke('notify-push', {
    body: {
      recipientIds: admins.map(a => a.id),
      type,
      title,
      body,
      link,
    },
  }).catch(() => {})
}

// ── Audit Logs ────────────────────────────────────────────────
export const getAuditLogs = async (filters?: {
  table?: string; userId?: string; action?: string
  from?: string; to?: string; limit?: number
}) => {
  let q = supabase.from('audit_logs')
    .select('*, profiles(id,display_name,email)')
    .order('created_at', { ascending: false })
    .limit(filters?.limit ?? 500)
  if (filters?.table)  q = q.eq('table_name', filters.table)
  if (filters?.userId) q = q.eq('performed_by', filters.userId)
  if (filters?.action) q = q.eq('action', filters.action)
  if (filters?.from)   q = q.gte('created_at', filters.from)
  if (filters?.to)     q = q.lte('created_at', filters.to)
  const { data, error } = await q
  if (error) throw error
  return data as AuditLog[]
}
